import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PortraitStage from '../components/PortraitStage';
import ScreenHeader from '../components/ScreenHeader';
import CircleButton from '../components/CircleButton';
import KioskModal from '../components/KioskModal';
import UploadQrModal from '../components/UploadQrModal';
import SubmitStatusModal from '../components/modals/SubmitStatusModal';
import UsernameModal from '../components/modals/UsernameModal';
import { ArrowLeft, ArrowRight, CameraIcon } from '../components/icons';
import { updateTicketNamesApi } from '../api/ticket';
import { emitSocialCollage } from '../services/socialCollageSocket';
import { saveContribution } from '../utils/contribution';
import { getImageOrientation } from '../utils/imageOrientation';
import {
  mergeTicketNames,
  readTicket,
  saveTicket,
  splitGuestName,
  ticketRecordNames,
} from '../utils/ticket';
import uploadIcon from '../assets/img/uplod-photo.png';

const MESSAGE_MAX = 50;

function ticketWithSavedName(navTicket) {
  const stored = readTicket();
  if (!navTicket) return stored;
  if (!stored) return navTicket;

  const navId = navTicket.id || navTicket.ticket?.id;
  const storedId = stored.id || stored.ticket?.id;
  if (navId && storedId && String(navId) !== String(storedId)) return navTicket;
  if (ticketRecordNames(navTicket).isEmpty && !ticketRecordNames(stored).isEmpty) {
    return mergeTicketNames(navTicket, ticketRecordNames(stored));
  }
  return navTicket;
}

function AddContributionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingTicket = ticketWithSavedName(location.state?.ticket);
  const [ticket, setTicket] = useState(incomingTicket);
  const [nameOpen, setNameOpen] = useState(() =>
    Boolean(incomingTicket) && ticketRecordNames(incomingTicket).isEmpty
  );
  const [guestName, setGuestName] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [readyOpen, setReadyOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const [photo, setPhoto] = useState('');
  const [photoSkipped, setPhotoSkipped] = useState(false);
  const [orientation, setOrientation] = useState('portrait');
  const [message, setMessage] = useState('');
  const [messageErr, setMessageErr] = useState('');
  const [submitPhase, setSubmitPhase] = useState('idle');
  const [submitErr, setSubmitErr] = useState('');
  const showMessageStep = Boolean(photo) || photoSkipped;

  const closeQr = useCallback(() => setQrOpen(false), []);
  const onUploaded = useCallback(async (url) => {
    setPhoto(url);
    setPhotoSkipped(false);
    setQrOpen(false);
    const nextOrientation = await getImageOrientation(url);
    setOrientation(nextOrientation);
  }, []);

  const skipPhoto = useCallback(() => {
    setPhoto('');
    setOrientation('portrait');
    setPhotoSkipped(true);
  }, []);

  const submitName = useCallback(async () => {
    if (!guestName.trim()) {
      setNameErr('Name field is required.');
      return;
    }
    setNameErr('');
    const { firstName, lastName } = splitGuestName(guestName);
    const names = {
      firstName,
      lastName: lastName || ' ',
      fullName: guestName.trim(),
    };
    const ticketId = ticket?.id || '';

    setNameSaving(true);
    try {
      if (ticketId) {
        await updateTicketNamesApi(ticketId, {
          first_name: names.firstName,
          last_name: names.lastName,
        });
      }
    } catch {
      /* Keep the name on this kiosk even if the ticket update fails. */
    } finally {
      const next = mergeTicketNames(ticket, names);
      saveTicket(next);
      setTicket(next);
      setNameOpen(false);
      setGuestName('');
      setNameSaving(false);
      navigate('/add-contribution', { replace: true, state: { ticket: next } });
    }
  }, [guestName, navigate, ticket]);

  useEffect(() => {
    if (submitPhase !== 'submitted') return undefined;
    const timer = window.setTimeout(() => {
      const names = ticketRecordNames(ticket);
      const entry = {
        photo,
        message: message.trim(),
        ticketId: ticket?.id || ticket?.ticket?.id || '',
        username: names.fullName,
        orientation,
      };
      saveContribution(entry);
      navigate('/thank-you', { state: entry });
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [message, navigate, orientation, photo, submitPhase, ticket]);

  const submitEntry = async () => {
    setReadyOpen(false);
    setSubmitErr('');
    setSubmitPhase('submitting');
    const names = ticketRecordNames(ticket);
    try {
      await emitSocialCollage({
        ticketId: ticket?.id || ticket?.ticket?.id || '',
        imageUrl: photo,
        text: message.trim(),
        username: names.fullName,
        orientation,
      });
      setSubmitPhase('submitted');
    } catch (error) {
      setSubmitPhase('idle');
      setSubmitErr(error?.message || 'Could not add your photo to the wall.');
    }
  };

  const confirmBack = () => {
    setBackOpen(false);
    if (showMessageStep) {
      setPhoto('');
      setPhotoSkipped(false);
      setOrientation('portrait');
      setMessage('');
      setMessageErr('');
      return;
    }
    navigate('/');
  };

  const addToWall = () => {
    if (!message.trim()) {
      setMessageErr('Message is required.');
      return;
    }
    setMessageErr('');
    setReadyOpen(true);
  };

  const backButton = (
    <button type="button" className="contribute__back" aria-label="Back" onClick={() => setBackOpen(true)}>
      <ArrowLeft />
    </button>
  );

  return (
    <PortraitStage>
      <section
        className={`contribute${showMessageStep ? ' contribute--message' : ''}${showMessageStep && !photo ? ' contribute--skip' : ''}`}
        aria-labelledby="contribute-title"
      >
        <ScreenHeader title="Mosida" subtitle="Social Collage" />

        {showMessageStep ? (
          <main className="contribute__main">
            <div className="contribute__titleRow">
              {backButton}
              <h1 id="contribute-title" className="contribute__title">
                {photo ? (
                  <>
                    Add a message
                    <br />
                    to your photo
                  </>
                ) : (
                  <>
                    Add a
                    <br />
                    message
                  </>
                )}
              </h1>
            </div>
            <p className="contribute__prompt">Your words will appear on the collective work.</p>

            {photo ? (
              <div className="contribute__photo">
                <img src={photo} alt="Your upload" />
              </div>
            ) : null}

            <div className="messageFieldWrap">
              <label className={`messageField${messageErr ? ' is-error' : ''}`}>
                <span className="messageField__icon" aria-hidden="true">
                  T
                </span>
                <input
                  className="messageField__input"
                  type="text"
                  value={message}
                  maxLength={MESSAGE_MAX}
                  onChange={(event) => {
                    setMessage(event.target.value);
                    if (event.target.value.trim()) setMessageErr('');
                  }}
                  placeholder="Add a message (max 50 characters)"
                  aria-label="Add a message (max 50 characters)"
                  aria-invalid={messageErr ? 'true' : 'false'}
                  required
                />
                <span className="messageField__count" aria-live="polite">
                  {message.length}/{MESSAGE_MAX}
                </span>
              </label>
              {messageErr ? (
                <p className="messageField__error" role="alert">
                  {messageErr}
                </p>
              ) : null}
            </div>

            {submitErr ? (
              <p className="messageField__error" role="alert">
                {submitErr}
              </p>
            ) : null}

            <div className="contribute__actions">
              <button
                type="button"
                className="contribute__reupload"
                onClick={() => setQrOpen(true)}
              >
                <CameraIcon />
                {photo ? 'Re-upload photo' : 'Upload photo'}
              </button>
              <CircleButton
                className="circleButton--wall"
                label={
                  <>
                    Add to
                    <br />
                    wall
                  </>
                }
                ariaLabel="Add to wall"
                icon={<ArrowRight />}
                onClick={addToWall}
              />
            </div>
          </main>
        ) : (
          <>
            {backButton}
            <main className="contribute__main">
              <h1 id="contribute-title" className="contribute__title">
                Add your
                <br />
                contribution
              </h1>
              <p className="contribute__prompt">Choose how you want to share:</p>

              <button
                type="button"
                className="contribute__upload"
                onClick={() => setQrOpen(true)}
                aria-label="Upload a photo"
              >
                <img className="contribute__uploadIcon" src={uploadIcon} alt="" />
              </button>

              <button type="button" className="contribute__skip" onClick={skipPhoto}>
                Skip
              </button>
            </main>
          </>
        )}

        <UploadQrModal open={qrOpen} onClose={closeQr} onUploaded={onUploaded} />

        <UsernameModal
          show={nameOpen}
          name={guestName}
          setName={setGuestName}
          nameErr={nameErr}
          setNameErr={setNameErr}
          onSubmit={submitName}
          loading={nameSaving}
        />

        <KioskModal
          show={readyOpen}
          onHide={() => setReadyOpen(false)}
          labelledBy="ready-title"
          confirm
        >
          <h2 id="ready-title" className="readyModal__title">
            Ready to submit your entry?
          </h2>
          <div className="readyModal__actions">
            <button
              type="button"
              className="readyModal__button readyModal__button--submit"
              onClick={submitEntry}
            >
              Yes, submit
            </button>
            <button
              type="button"
              className="readyModal__button readyModal__button--edit"
              onClick={() => setReadyOpen(false)}
            >
              Keep editing
            </button>
          </div>
        </KioskModal>

        <KioskModal
          show={backOpen}
          onHide={() => setBackOpen(false)}
          labelledBy="back-title"
          confirm
        >
          <h2 id="back-title" className="readyModal__title">
            Are you sure you want to back?
          </h2>
          <div className="readyModal__actions">
            <button
              type="button"
              className="readyModal__button readyModal__button--submit"
              onClick={confirmBack}
            >
              Yes
            </button>
            <button
              type="button"
              className="readyModal__button readyModal__button--edit"
              onClick={() => setBackOpen(false)}
            >
              No
            </button>
          </div>
        </KioskModal>

        <SubmitStatusModal phase={submitPhase} />
      </section>
    </PortraitStage>
  );
}

export default AddContributionScreen;
